/*
 * Copyright (c) 2025. Jellybean B.V.
 * Jellybean B.V. Apeldoorn heeft een licentie verleend aan
 * Spin in het Web B.V. Apeldoorn voor het gebruik van deze software-
 * onderdelen in projecten voor derden. U heeft géén toestemming
 * tot inzage of wijziging in de (bron)code van deze onderdelen.
 */

/*
* sihwdatepicker.ts
* 8-3-2020 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component, HostBinding, Input} from '@angular/core';
import moment from 'moment';
import {isMoment, Moment} from 'moment';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {Sihwlog, Sihwlogger} from '../../sihwlog/sihwlog';
import {BsLocaleService} from 'ngx-bootstrap/datepicker';

// datepicker locales die gekozen kunnen worden (eenmalige init)
import {defineLocale} from 'ngx-bootstrap/chronos';
import {nlLocale} from 'ngx-bootstrap/locale';

defineLocale('nl', nlLocale);


@Component({
  selector: 'sihwdatepicker',
  templateUrl: './sihwdatepicker.html',
  styleUrls: ['./sihwdatepicker.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: SihwDatepicker, multi: true},
    {provide: NG_VALIDATORS, useExisting: SihwDatepicker, multi: true} //doorgeven valid state
  ],
  standalone: false
})
export class SihwDatepicker implements ControlValueAccessor, Validator {
  static idTeller = 1;
  ctrlid = `sihwdatepicker_${SihwDatepicker.idTeller++}`;

  @Input('bsConfig') bsConfig_in = {}; //config om door de geven aan de datepicker (zie https://valor-software.com/ngx-bootstrap/#/datepicker)
  @Input('dateType') dateType: ('mysql' | 'moment') = 'mysql'; //waarde in sql-string of moment

  //minDate en maxDate zijn setters op interne vars, zodat we validiteit kunnen herchecken
  private _minDate: string | Moment | null = null;
  private _maxDate: string | Moment | null = null;

  //zie getters en setters:
  //@Input('minDate') minDate?: string|Moment; //sql of moment
  //@Input('maxDate') maxDate?: string|Moment; //sql of moment
  //copy props
  @Input() placement?: 'top' | 'bottom' | 'left' | 'right';
  @Input() placeholder: string = '';


  date: Date | string | null = null; //de interne value (string bij ongeldig)
  @Input('disabled') disabled: boolean = false; //de disabled value, en readonly op de host. Dat gebruiken we als alternatief voor disabled, wat we niet goed kunnen zetten
  @HostBinding('attr.readonly') get hostReadonly() {
    return this.disabled ? true : null;
  } //zorg voor verwijderen readonly als niet nodig

  private log: Sihwlogger;

  //////////////////////////////// MODEL /////////////////////////////
  gewijzigd(_nieuwewaarde: any) {
    if (! this.date)
    {
      this.date = null;
      this.meldGewijzigd(null);
      return;
    }
    let nieuw = this.datumMoment(this.date);
    if (!nieuw.isValid()) {
      this.meldGewijzigd(this.date);
      return;
    }
    this.log.debug(`Value nu`, nieuw.format('YYYY-MM-DD'));
    this.meldGewijzigd(this.dateType === 'mysql' ? nieuw.format('YYYY-MM-DD') : nieuw);
  }
  onTouched: () => void = () => {
  }; //controlvalueaccessor

  private hostControl?: AbstractControl; //wordt gezet via de validator (dirty hack die het leven makkelijker maakt)

  constructor(localeService: BsLocaleService, sihwlog: Sihwlog) {
    localeService.use('nl'); //locale voor de datepicker. Kan optie worden
    this.log = sihwlog.logger();
  }

  get bsConfig(): any {
    //paar dingen aanpassen
    return Object.assign(
      {
        isAnimated: true,
        adaptivePosition: true,
        containerClass: 'theme-default'
        //overschrijfbare zaken
      },
      this.bsConfig_in,
      {
        //zaken die we zo houden als ze gegeven zijn
      }
    );
  }

  ////////////////////////////////////// input getters en setters

  //@Input('minDate') minDate?: string|Moment; //sql of moment
  //@Input('maxDate') maxDate?: string|Moment; //sql of moment

  get minDate(): string | Moment | null {
    return this._minDate;
  };

  @Input('minDate') set minDate(d: string | Moment | null) {

    if (d === 'now') {
      this._minDate = moment().format("YYYY-MM-DD");
    } else if (d) {
      this._minDate = d;
    }
    else {
      this._minDate = null;
    }
    //validate als dat kan
    this.revalidate();
  }

  get maxDate(): string | Moment | null {
    return this._maxDate;
  }

  @Input('maxDate') set maxDate(d: string | Moment | null) {
    if (d === 'now') {
      this._maxDate = moment().format("YYYY-MM-DD");
    } else if (d) {
      this._maxDate = d;
    }
    else {
      this._maxDate = null;
    }
    //validate als dat kan
    this.revalidate();
  }

  ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
  /**
   * Nieuwe waarde wordt aangeboden
   * @param obj
   */
  writeValue(obj: any): void {
    //de waarde komt als moment of als mysqstring binnen, dat regelen we
    this.date = this.toDate(obj);
    this.log.debug(`sihwpdatepicker set value`, obj, this.date);
  }


  registerOnChange(fn: any): void {
    this.meldGewijzigd = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.log.debug(`sihwdatepicker setDisabled`, isDisabled);
    this.disabled = isDisabled;
  }

  //////////////////////////////// VALIDATOR /////////////////////////
  validate(control: AbstractControl): ValidationErrors | null {
    //ha, onze host control
    if (!this.hostControl) {
      this.hostControl = control;
    }

    //gaat het goed met date?
    this.log.debug(`sihwdatepicker. Valid?`, this.date);
    if (this.date == null) {
      this.log.debug('- leeg dus okee');
      return null; //prima - leeg maar prima
    }

    let m = this.datumMoment(this.date);
    if (!m.isValid()) {
      this.log.debug('- invalid date');
      return {'invalid_date': true};
    }

    if (this.minDate && m.isBefore(this.datumMoment(this.minDate))) {
      this.log.warn(`Datepicker: voor de mindate`);
      return {'mindate': true};
    }
    if (this.maxDate && m.isAfter(this.datumMoment(this.maxDate))) {
      this.log.warn(`Datepicker: na de maxdate`, m, this.datumMoment(this.maxDate));
      return {'maxdate': true};
    }

    this.log.debug('- ja, okee');
    return null;
  }

  /////// eigen helper
  revalidate() {
    //als we al een keer gevalideerd hebben, hebben we de control
    if (this.hostControl) {
      this.hostControl.updateValueAndValidity();
    }
    //anders komt er nog wel een begin-validering
  }

  private meldGewijzigd: (_: any) => void = (_) => {}; //controlvalueaccessor

  //////////////////////////// helper ///////////////////////////////
  /**
   * Zet een val (mysqldate of moment) om in een js date. Of null als het null is
   * @param val
   */
  toDate(val: string | Moment | null): Date | null {
    if (!val) {
      //null etc
      return null;
    }

    return this.datumMoment(val).toDate();
  }

  //er is wat gedoe met utc, en het vergelijken ervan
  //maar het lijkt toch beter te gaan om het direct via moment te laten lopen
  //de truuk is denk ik om altijd in de huidige tz te gaan
  datumMoment(m: string | Date | Moment | null) {
    if (!isMoment(m)) {
      m = moment(m);
    }
    if (!m.isValid()) {
      return m; //gewoon invalid moment
    }
    return moment(m.format('YYYY-MM-DD')); //rewrite naar onze eigen tz
  }

}
